import React, { useState, useEffect } from 'react';
import { X, Copy, Check } from 'lucide-react';
import { affiliateService } from '../../services/affiliateService';
import { Toast } from '../Toast';

interface AffiliateOnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDecline: () => void;
}

export const AffiliateOnboardingModal: React.FC<AffiliateOnboardingModalProps> = ({
  isOpen,
  onClose,
  onDecline
}) => {
  const [affiliateId, setAffiliateId] = useState<string>('');
  const [affiliateLink, setAffiliateLink] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [copySuccess, setCopySuccess] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadAffiliateData();
    }
  }, [isOpen]);

  const loadAffiliateData = async () => {
    setLoading(true);
    try {
      const response = await affiliateService.getAffiliateData();
      if (response && response.affiliate) {
        setAffiliateId(response.affiliate.affiliateId);
        const link = affiliateService.generateAffiliateLink(response.affiliate.affiliateId);
        setAffiliateLink(link);
      }
    } catch (error) {
      // console.error supprimé
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!affiliateId) return;

    const success = await affiliateService.copyAffiliateLink(affiliateId);

    if (success) {
      setCopySuccess(true);
      setToast({ message: 'Lien copié !', type: 'success' });
      setTimeout(() => setCopySuccess(false), 2000);
    } else {
      setToast({ message: 'Erreur lors de la copie', type: 'error' });
    }
  };

  const handleDecline = () => {
    onDecline();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100]">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity" 
        onClick={handleDecline}
      />

      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <div className="bento-card w-full max-w-2xl max-h-[90vh] flex flex-col relative overflow-hidden shadow-2xl border border-white/[0.03]">
          
          {/* Header */}
          <div className="relative p-6 border-b border-white/[0.03] dark:border-white/[0.03] border-gray-200">
            <div className="flex items-center justify-center">
              <div className="flex items-center gap-3">
                <img 
                  src="https://firebasestorage.googleapis.com/v0/b/redline-e1775.firebasestorage.app/o/global%2Flaurel-left.png?alt=media&token=debc99ff-36f7-466c-9715-d3c5f0a7b798" 
                  alt="" 
                  className="w-6 h-10 sm:w-8 sm:h-14 opacity-80" 
                />
                <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-white whitespace-nowrap">
                  Rejoindre FocusPips Affiliate™
                </h1>
                <img 
                  src="https://firebasestorage.googleapis.com/v0/b/redline-e1775.firebasestorage.app/o/global%2Flaurel-right.png?alt=media&token=b3220939-a10e-4e51-a493-3d84027d049a" 
                  alt="" 
                  className="w-6 h-10 sm:w-8 sm:h-14 opacity-80" 
                />
              </div>
            </div>
            
            {/* Close button */}
            <button
              onClick={handleDecline}
              className="absolute right-4 top-4 p-2 rounded-lg text-content-secondary hover:text-white hover:bg-white/5 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Hero text */}
            <div className="text-center">
              <p className="text-content-secondary">
                Commence à générer dès maintenant un revenu passif en recommandant ton outil d'analyse trading performant.
              </p>
            </div>

            {/* Affiliate Link Section - Déplacé en haut */}
            <div className="bg-gray-50 dark:bg-[#0a0a0f] rounded-xl p-6 space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Ton lien d'affiliation</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Partage ce lien pour commencer à gagner des commissions
                </p>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-primary"></div>
                </div>
              ) : (
                <div className="flex gap-2 items-center px-4 py-3 rounded-lg bg-white dark:bg-[#0a0a0d] border border-gray-200 dark:border-white/[0.03]">
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <p className="text-sm font-mono truncate text-content-secondary">
                      {affiliateLink}
                    </p>
                  </div>
                  <button
                    onClick={handleCopyLink}
                    className="w-10 h-10 flex items-center justify-center rounded-lg transition-all hover:bg-white/5 flex-shrink-0"
                    title="Copier le lien"
                  >
                    {copySuccess ? (
                      <Check className="w-5 h-5 text-green-500" />
                    ) : (
                      <Copy className="w-5 h-5 text-brand-primary" />
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* How it works */}
            <div className="bg-gray-50 dark:bg-[#0a0a0f] rounded-xl p-6 space-y-6">
              <div className="text-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Comment fonctionne le programme</h2>
                <p className="text-sm text-content-secondary">
                  Voici les étapes et les avantages du programme de recommandation
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Step 1: Share */}
                <div className="text-center space-y-3 p-4 rounded-lg bg-white dark:bg-[#0a0a0d] border border-gray-200 dark:border-white/[0.03]">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-8 h-8">
                      <defs>
                        <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#7C76FF" />
                          <stop offset="50%" stopColor="#BA4BDB" />
                          <stop offset="100%" stopColor="#FF7AD9" />
                        </linearGradient>
                      </defs>
                      <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                      <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
                      <g id="SVGRepo_iconCarrier">
                        <rect width="24" height="24" fill="none"></rect>
                        <path fillRule="evenodd" clipRule="evenodd" d="M5 9.5C5 7.01472 7.01472 5 9.5 5C11.9853 5 14 7.01472 14 9.5C14 11.9853 11.9853 14 9.5 14C7.01472 14 5 11.9853 5 9.5Z" fill="url(#gradient1)"></path>
                        <path d="M14.3675 12.0632C14.322 12.1494 14.3413 12.2569 14.4196 12.3149C15.0012 12.7454 15.7209 13 16.5 13C18.433 13 20 11.433 20 9.5C20 7.567 18.433 6 16.5 6C15.7209 6 15.0012 6.2546 14.4196 6.68513C14.3413 6.74313 14.322 6.85058 14.3675 6.93679C14.7714 7.70219 15 8.5744 15 9.5C15 10.4256 14.7714 11.2978 14.3675 12.0632Z" fill="url(#gradient1)"></path>
                        <path fillRule="evenodd" clipRule="evenodd" d="M4.64115 15.6993C5.87351 15.1644 7.49045 15 9.49995 15C11.5112 15 13.1293 15.1647 14.3621 15.7008C15.705 16.2847 16.5212 17.2793 16.949 18.6836C17.1495 19.3418 16.6551 20 15.9738 20H3.02801C2.34589 20 1.85045 19.3408 2.05157 18.6814C2.47994 17.2769 3.29738 16.2826 4.64115 15.6993Z" fill="url(#gradient1)"></path>
                        <path d="M14.8185 14.0364C14.4045 14.0621 14.3802 14.6183 14.7606 14.7837V14.7837C15.803 15.237 16.5879 15.9043 17.1508 16.756C17.6127 17.4549 18.33 18 19.1677 18H20.9483C21.6555 18 22.1715 17.2973 21.9227 16.6108C21.9084 16.5713 21.8935 16.5321 21.8781 16.4932C21.5357 15.6286 20.9488 14.9921 20.0798 14.5864C19.2639 14.2055 18.2425 14.0483 17.0392 14.0008L17.0194 14H16.9997C16.2909 14 15.5506 13.9909 14.8185 14.0364Z" fill="url(#gradient1)"></path>
                      </g>
                    </svg>
                  </div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">Partage</h4>
                  <p className="text-sm text-content-secondary">
                    Partage ton lien unique avec tes contacts, ton réseau, tes amis
                  </p>
                </div>

                {/* Step 2: Commissions */}
                <div className="text-center space-y-3 p-4 rounded-lg bg-white dark:bg-[#0a0a0d] border border-gray-200 dark:border-white/[0.03]">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-8 h-8">
                      <defs>
                        <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#7C76FF" />
                          <stop offset="50%" stopColor="#BA4BDB" />
                          <stop offset="100%" stopColor="#FF7AD9" />
                        </linearGradient>
                      </defs>
                      <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                      <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
                      <g id="SVGRepo_iconCarrier">
                        <path d="M11.25 7.84748C10.3141 8.10339 9.75 8.82154 9.75 9.5C9.75 10.1785 10.3141 10.8966 11.25 11.1525V7.84748Z" fill="url(#gradient2)"></path>
                        <path d="M12.75 12.8475V16.1525C13.6859 15.8966 14.25 15.1785 14.25 14.5C14.25 13.8215 13.6859 13.1034 12.75 12.8475Z" fill="url(#gradient2)"></path>
                        <path fillRule="evenodd" clipRule="evenodd" d="M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12ZM12 5.25C12.4142 5.25 12.75 5.58579 12.75 6V6.31673C14.3804 6.60867 15.75 7.83361 15.75 9.5C15.75 9.91421 15.4142 10.25 15 10.25C14.5858 10.25 14.25 9.91421 14.25 9.5C14.25 8.82154 13.6859 8.10339 12.75 7.84748V11.3167C14.3804 11.6087 15.75 12.8336 15.75 14.5C15.75 16.1664 14.3804 17.3913 12.75 17.6833V18C12.75 18.4142 12.4142 18.75 12 18.75C11.5858 18.75 11.25 18.4142 11.25 18V17.6833C9.61957 17.3913 8.25 16.1664 8.25 14.5C8.25 14.0858 8.58579 13.75 9 13.75C9.41421 13.75 9.75 14.0858 9.75 14.5C9.75 15.1785 10.3141 15.8966 11.25 16.1525V12.6833C9.61957 12.3913 8.25 11.1664 8.25 9.5C8.25 7.83361 9.61957 6.60867 11.25 6.31673V6C11.25 5.58579 11.5858 5.25 12 5.25Z" fill="url(#gradient2)"></path>
                      </g>
                    </svg>
                  </div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">Commissions</h4>
                  <p className="text-sm text-content-secondary">
                    Tu gagnes 30% de commission à vie sur chaque abonnement que tu as recommandé !
                  </p>
                </div>

                {/* Step 3: Passive Income */}
                <div className="text-center space-y-3 p-4 rounded-lg bg-white dark:bg-[#0a0a0d] border border-gray-200 dark:border-white/[0.03]">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-8 h-8">
                      <defs>
                        <linearGradient id="gradient3" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#7C76FF" />
                          <stop offset="50%" stopColor="#BA4BDB" />
                          <stop offset="100%" stopColor="#FF7AD9" />
                        </linearGradient>
                      </defs>
                      <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                      <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
                      <g id="SVGRepo_iconCarrier">
                        <path fillRule="evenodd" clipRule="evenodd" d="M3.46447 3.46447C2 4.92893 2 7.28595 2 12C2 16.714 2 19.0711 3.46447 20.5355C4.92893 22 7.28595 22 12 22C16.714 22 19.0711 22 20.5355 20.5355C22 19.0711 22 16.714 22 12C22 7.28595 22 4.92893 20.5355 3.46447C19.0711 2 16.714 2 12 2C7.28595 2 4.92893 2 3.46447 3.46447ZM5.46058 11.0833C5.83333 7.79988 8.62406 5.25 12.0096 5.25C13.9916 5.25 15.7702 6.12471 16.9775 7.50653C17.25 7.81846 17.2181 8.29226 16.9061 8.56479C16.5942 8.83733 16.1204 8.80539 15.8479 8.49347C14.9136 7.42409 13.541 6.75 12.0096 6.75C9.45215 6.75 7.33642 8.63219 6.97332 11.0833H7.33654C7.63998 11.0833 7.91353 11.2662 8.02955 11.5466C8.14558 11.8269 8.08122 12.1496 7.86651 12.364L6.69825 13.5307C6.40544 13.8231 5.93113 13.8231 5.63832 13.5307L4.47005 12.364C4.25534 12.1496 4.19099 11.8269 4.30701 11.5466C4.42304 11.2662 4.69658 11.0833 5.00002 11.0833H5.46058ZM17.3018 10.4693C17.5947 10.1769 18.069 10.1769 18.3618 10.4693L19.53 11.636C19.7448 11.8504 19.8091 12.1731 19.6931 12.4534C19.5771 12.7338 19.3035 12.9167 19.0001 12.9167H18.5395C18.1668 16.2001 15.376 18.75 11.9905 18.75C10.0085 18.75 8.22995 17.8753 7.02263 16.4935C6.7501 16.1815 6.78203 15.7077 7.09396 15.4352C7.40589 15.1627 7.87968 15.1946 8.15222 15.5065C9.08654 16.5759 10.4591 17.25 11.9905 17.25C14.548 17.25 16.6637 15.3678 17.0268 12.9167H16.6636C16.3601 12.9167 16.0866 12.7338 15.9705 12.4534C15.8545 12.1731 15.9189 11.8504 16.1336 11.636L17.3018 10.4693Z" fill="url(#gradient3)"></path>
                      </g>
                    </svg>
                  </div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">Revenus Passifs</h4>
                  <p className="text-sm text-content-secondary">
                    Génère facilement et automatiquement des revenus récurrents chaque mois
                  </p>
                </div>
              </div>
            </div>

            {/* Description text */}
            <div className="text-center px-4">
              <p className="text-sm text-content-secondary">
                En rejoignant le programme FocusPips Affiliate™, tu ne gagnes plus une simple réduction.
                Tu touches <span className="text-brand-primary font-semibold">30% de commission à vie</span> sur chaque abonné que tu recommandes.
                Paiements mensuels. Revenus récurrents. Outil performant.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-200 dark:border-white/[0.03] flex justify-end">
            <button
              onClick={handleDecline}
              className="text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors px-4 py-2"
            >
              Non, merci
            </button>
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default AffiliateOnboardingModal;
